/**
 * Space Invite Accept Page
 *
 * Handles the /invite/space/:token flow for collaboration space invitations.
 *
 * Scenarios:
 *  A. User is already logged in  → accept immediately, redirect to the app.
 *  B. User is not logged in      → prompt to log in, then accept + redirect.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Loader2,
  CheckCircle,
  XCircle,
  Layers,
  LogIn,
  User,
  Users,
  Building2,
} from 'lucide-react';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { acceptSpaceInvitation } from '../../lib/spaceService';
import type { SpaceType } from '../../types/prioritization/collaborationSpaces';

type PageState = 'loading' | 'login-required' | 'accepting' | 'success' | 'error';

function SpaceTypeIcon({ type }: { type: SpaceType }) {
  if (type === 'personal') return <User className="h-5 w-5" />;
  if (type === 'team') return <Users className="h-5 w-5" />;
  return <Building2 className="h-5 w-5" />;
}

interface InvitePreview {
  spaceName: string;
  spaceType: SpaceType;
  role: string;
}

export default function SpaceInviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [spaceName, setSpaceName] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState('');

  // ── On mount: check auth state and invitation validity ──────────────────────

  useEffect(() => {
    async function init() {
      if (!token) {
        setErrorMessage('No invitation token provided.');
        setPageState('error');
        return;
      }

      const supabase = getSupabaseClient();

      // Look up the invitation to get preview data (works without auth via RLS on space_invitations)
      const { data: inv } = await supabase
        .from('space_invitations')
        .select('role, expires_at, space_id, spaces(name, space_type)')
        .eq('token', token)
        .maybeSingle();

      if (!inv) {
        setErrorMessage('This invitation link is invalid or has already been used.');
        setPageState('error');
        return;
      }

      if (new Date(inv.expires_at) < new Date()) {
        setErrorMessage('This invitation has expired. Ask the space owner to create a new one.');
        setPageState('error');
        return;
      }

      const space = inv.spaces as { name: string; space_type: SpaceType } | null;
      if (space) {
        setPreview({
          spaceName: space.name,
          spaceType: space.space_type,
          role: inv.role,
        });
        setSpaceName(space.name);
      }

      // Check if the user is already authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setPageState('login-required');
        return;
      }

      // Logged in — accept immediately
      await acceptInvite(token);
    }

    init();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function acceptInvite(tok: string) {
    setPageState('accepting');
    const result = await acceptSpaceInvitation(tok);
    if (result.success) {
      setSpaceName(result.spaceName ?? spaceName);
      setPageState('success');
      setTimeout(() => navigate('/prioritization', { replace: true }), 1800);
    } else {
      setErrorMessage(result.error ?? 'Could not accept this invitation.');
      setPageState('error');
    }
  }

  // ── Login then accept ───────────────────────────────────────────────────────

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setLoggingIn(true);
    setLoginError('');
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) {
      setLoginError(error.message);
      setLoggingIn(false);
      return;
    }
    await acceptInvite(token);
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const ROLE_LABELS: Record<string, string> = {
    owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer', guest: 'Guest',
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface)] px-4">
      <div className="w-full max-w-md space-y-4">

        {/* Loading */}
        {(pageState === 'loading' || pageState === 'accepting') && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--slate-dim)] rounded-full mb-4">
              <Loader2 className="h-8 w-8 text-[var(--text-ter)] animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
              {pageState === 'accepting' ? 'Joining space…' : 'Checking invitation…'}
            </h2>
            <p className="text-[var(--text-ter)] text-sm">Just a moment.</p>
          </div>
        )}

        {/* Login required */}
        {pageState === 'login-required' && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-sm overflow-hidden">
            <div className="px-8 pt-8 pb-6 border-b border-[var(--rule)]">
              <div className="flex items-center gap-3 mb-1">
                <div className="p-2 bg-[var(--slate-dim)]">
                  <Layers className="h-5 w-5 text-[var(--text-ter)]" />
                </div>
                <h1 className="text-xl font-semibold text-[var(--text-pri)]">You're invited to a space</h1>
              </div>
              <p className="text-sm text-[var(--text-ter)] mt-1">Sign in to accept your invitation.</p>
            </div>

            {/* Invitation preview */}
            {preview && (
              <div className="px-8 py-4 bg-[var(--surface)] border-b border-[var(--rule)] flex items-start gap-3">
                <div className="p-2 bg-[var(--bg-raised)] border border-[var(--rule)] text-[var(--text-ter)] flex-shrink-0">
                  <SpaceTypeIcon type={preview.spaceType} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[var(--text-pri)]">{preview.spaceName}</div>
                  <div className="text-xs text-[var(--text-ter)] mt-0.5 capitalize">
                    {preview.spaceType} space · Role: {ROLE_LABELS[preview.role] ?? preview.role}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleLogin} className="px-8 py-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-[var(--slate)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Your password"
                  required
                  className="w-full px-4 py-3 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-[var(--slate)]"
                />
              </div>

              {loginError && (
                <p className="text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] rounded px-3 py-2">
                  {loginError}
                </p>
              )}

              <button
                type="submit"
                disabled={loggingIn}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] transition-colors disabled:opacity-50"
              >
                <LogIn className="h-4 w-4" />
                {loggingIn ? 'Signing in…' : 'Sign in & join space'}
              </button>

              <p className="text-center text-xs text-[var(--text-ter)]">
                Don't have an account?{' '}
                <Link to="/auth/login" className="text-[var(--text-ter)] hover:underline">Contact your workspace admin</Link>
              </p>
            </form>
          </div>
        )}

        {/* Success */}
        {pageState === 'success' && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--green-dim)] rounded-full mb-4">
              <CheckCircle className="h-8 w-8 text-[var(--green)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">You're in!</h2>
            <p className="text-[var(--text-ter)] text-sm mb-4">
              You've joined <span className="font-medium text-[var(--text-pri)]">{spaceName || 'the space'}</span>. Taking you to the app…
            </p>
            <Loader2 className="h-5 w-5 text-[var(--text-ter)] animate-spin mx-auto" />
          </div>
        )}

        {/* Error */}
        {pageState === 'error' && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--coral-dim)] rounded-full mb-4">
              <XCircle className="h-8 w-8 text-[var(--coral)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">Invitation problem</h2>
            <p className="text-[var(--coral)] text-sm mb-6">{errorMessage}</p>
            <Link
              to="/auth/login"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors text-sm font-medium"
            >
              <LogIn className="h-4 w-4" />
              Go to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
